import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { HOST_PROYECTO, obtenerUrlFirmada } from "@/hooks/useUrlFirmada";
import { idDocumentoMifiel, rutaStorageDeRecurso } from "@/lib/rutaRecurso";

/**
 * Resuelve la URL que un visor puede abrir: firma el bucket privado, resuelve documentos
 * Mifiel por Edge Function o deja pasar la URL pública. Lo usan `ModalViewer` y
 * `ModalViewerDetail` (la lógica no vive en `ui/`).
 */
export function useUrlRecurso(open: boolean, url: string | null | undefined) {
  const [resolved, setResolved] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !url) {
      setResolved(null);
      setError(null);
      return;
    }
    const mifiel = idDocumentoMifiel(url);
    if (mifiel) {
      setLoading(true);
      setError(null);
      supabase.functions
        .invoke("mifiel-consultar-documento", { body: { document_id: mifiel } })
        .then(({ data, error: err }) => {
          const r = data?.signed_pdf_url || data?.pdf_storage_url || null;
          if (err || !data?.success || !r) {
            setError("No se pudo abrir el documento.");
            setResolved(null);
            return;
          }
          setResolved(r);
        })
        .finally(() => setLoading(false));
      return;
    }
    const info = rutaStorageDeRecurso(url, HOST_PROYECTO);
    if (!info) {
      setResolved(url);
      return;
    }
    setLoading(true);
    setError(null);
    obtenerUrlFirmada(info)
      .then((firmada) => {
        if (firmada) setResolved(firmada);
        // Mientras el bucket siga publico la URL guardada abre; ruta relativa privada fallida
        // -> error controlado (no cargar la ruta cruda -> 404 del SPA).
        else if (/^https?:\/\//i.test(url)) setResolved(url);
        else {
          setResolved(null);
          setError("No se pudo abrir el documento.");
        }
      })
      .finally(() => setLoading(false));
  }, [open, url]);

  return { url: resolved || "", loading, error };
}
